/*@flow*/
import type { Metric, MapConfig, Context } from './types';
import Present from 'lib/Present';
import getMapConfig from './getMapConfig';
import * as t from 'lib/t';
import * as conversions from './conversions';
import * as I from 'immutable';
import intersectFn from './intersectFn';
import reorder from './reorder';
import type { CartoError } from 'lib/getCartoMap';
import getCartoSQL from 'lib/getCartoSQL';
import CustomError from 'lib/CustomError';
import objectToCSVRow from 'lib/objectToCSVRow';
import type {
  PlaceMetricCreateInput,
  PlaceMetricUpdateInput,
} from './__generated__/CreatePlaceMetric';
import intersectGeoJSON from 'lib/intersectGeoJSON';
import downloadCSV from 'lib/downloadCSV';
import evalFormula from './evalFormula';

const commands = (ctx: Context): {|
  applyIntersection: () => void,
  createMap: (mapConfig: MapConfig) => any,
  createPlaceMetric: (input: PlaceMetricCreateInput) => Promise<void>,
  deleteWidget: (id: string) => void,
  doneCustomizing: () => void,
  doneEditing: () => void,
  downloadAreaData: (id: string) => any,
  editWidget: (id: string) => void,
  init: (o: any) => void,
  newWidget: () => void,
  reorder: (source: number, destination: number) => void,
  saveCartoError: (e: CartoError) => void,
  serverError: (e: any) => void,
  set: (path: string, value: any) => void,
  setGeoJSON: (geojson: ?string) => void,
  startCustomizing: () => void,
  tipsDismissed: () => void,
  toggleSelected: (id: $PropertyType<Metric, "id">) => void,
  updateMapConfig: () => void,
  updatePlaceMetric: (input: PlaceMetricUpdateInput) => Promise<void>,
  updatePlaceMetricsOrder: (ids: Array<string>) => void,
  updateSelectedMetrics: (ids: Array<string>) => void,
|} => ({
  set(path: string, value: any) {
    ctx.dispatch({
      type: 'SET_RECORD',
      payload: { path, value },
    });
  },
  init(o: Object) {
    const {
      selectedMetrics,
      placeMetricsOrder,
      me: { company },
    } = o;
    const { placeMetricAnalyses: tables, placeMetrics } = company;
    const ids = placeMetrics.map(v => v.id);
    const orderedIds = Present.is(placeMetricsOrder) ? placeMetricsOrder : ids;

    const payload = {
      placeMetrics,
      placeMetricsOrder: orderedIds,
      tables,
      mapConfig: getMapConfig(placeMetrics),
      selectedMetrics,
    };
    ctx.dispatch({
      type: 'MERGE_RECORD',
      payload,
    });
  },
  newWidget() {
    ctx.dispatch({ type: 'NEW_REQUESTED' });
  },
  editWidget(id: string) {
    // Obtain metric from state
    const { placeMetrics } = ctx.state;
    const metric = placeMetrics.find(v => String(v.id) === String(id));
    t.invariant(metric, t.Object, 'Widget id not found');
    if (!metric) return;
    const True = t.subtype(t.Boolean, v => v);
    const IsCustom = t.union([t.Nil, True]);
    // Sanity check
    t.invariant(metric.custom, IsCustom, 'Cannot edit a preset widget.');
    // Transform metric to shape of the form.
    const payload = conversions.metricToForm(metric);
    ctx.dispatch({ type: 'EDIT_REQUESTED', payload });
  },
  deleteWidget(id: string) {
    const { handlers } = ctx.state;
    const { destroyPlaceMetric } = handlers;
    if (!destroyPlaceMetric) return;

    destroyPlaceMetric({ variables: { input: { id } } })
      .then(res => {
        const { success, errors } = res.data.destroyPlaceMetric;
        if (success) {
          ctx.dispatch({ type: 'DATA_PANEL_DELETED', payload: id });
        } else {
          commands(ctx).serverError(errors);
        }
      })
      .catch(err => {
        commands(ctx).serverError(err);
      });
  },
  startCustomizing() {
    commands(ctx).set('mode', 'CUSTOMIZING');
  },
  doneCustomizing() {
    commands(ctx).set('mode', 'READY');
  },
  updatePlaceMetric(input: PlaceMetricUpdateInput) {
    const { handlers } = ctx.state;
    const { updatePlaceMetric } = handlers;
    return updatePlaceMetric({ variables: { input } })
      .then(res => {
        const { success, record, errors } = res.data.updatePlaceMetric;
        if (success) {
          ctx.dispatch({
            type: 'DATA_PANEL_MUTATED',
            payload: record,
          });
        } else {
          commands(ctx).serverError(errors);
        }
      })
      .catch(err => {
        commands(ctx).serverError(err);
      });
  },
  createPlaceMetric(input: PlaceMetricCreateInput) {
    const { handlers } = ctx.state;
    const { createPlaceMetric } = handlers;
    return createPlaceMetric({ variables: { input } })
      .then(res => {
        const { record, success, errors } = res.data.createPlaceMetric;
        if (success) {
          ctx.dispatch({
            type: 'DATA_PANEL_CREATED',
            payload: record,
          });
        } else {
          commands(ctx).serverError(errors);
        }
      })
      .catch(err => {
        commands(ctx).serverError(err);
      });
  },
  doneEditing() {
    const { widgetForm, handlers } = ctx.state;
    // Transform widgetForm to metric
    const metric = conversions.formToMetric(widgetForm);
    const { createPlaceMetric, updatePlaceMetric } = handlers;

    if (metric.id && updatePlaceMetric) {
      const input = ((metric: any): PlaceMetricUpdateInput);
      commands(ctx).updatePlaceMetric(input);
    } else if (createPlaceMetric) {
      const input = ((metric: any): PlaceMetricCreateInput);
      commands(ctx).createPlaceMetric(input);
    }
  },
  tipsDismissed() {
    commands(ctx).set('tipsDismissed', true);
  },
  createMap(mapConfig: MapConfig) {
    const task = ctx.getCartoMap(mapConfig);
    const taskExecution = task.run().listen({
      onResolved(v) {
        ctx.dispatch({
          type: 'MAP_CREATED',
          payload: v,
        });
      },
      onRejected(e: CustomError<CartoError>) {
        let msg = e.message;
        if (!msg) return;
        if (typeof msg === 'string') {
          try {
            msg = JSON.parse(msg);
            commands(ctx).saveCartoError(msg);
          } catch (_) {
            window.Rollbar.error(msg);
          }
        } else {
          commands(ctx).saveCartoError(msg);
        }
      },
      onCancelled() {},
    });

    return taskExecution;
  },
  saveCartoError(e: CartoError) {
    ctx.dispatch({
      type: 'MAP_FAILED',
      payload: e,
    });
  },
  serverError(e: any) {
    ctx.dispatch({
      type: 'DATA_PANEL_MUTATION_ERROR',
      payload: e,
    });
  },
  updateMapConfig() {
    const mapConfig = getMapConfig(ctx.state.placeMetrics);
    commands(ctx).set('mapConfig', mapConfig);
  },
  setGeoJSON(geojson: ?string) {
    if (geojson) {
      const geom = JSON.parse(geojson);
      // Extract geometry from Feature
      if (geom.type === 'Feature') {
        geojson = JSON.stringify(geom.geometry);
      }
    }

    ctx.dispatch({
      type: 'GEOM_CHANGED',
      payload: geojson,
    });
  },
  applyIntersection() {
    const mapConfig = I.fromJS(ctx.state)
      .update(intersectFn)
      .get('mapConfig')
      .toJS();

    commands(ctx).createMap(mapConfig);
  },
  reorder(source: number, destination: number) {
    let { placeMetrics, selectedMetrics } = ctx.state;
    placeMetrics = reorder(placeMetrics, source, destination);
    let allIds = placeMetrics.map(v => v.id);
    const selectedIds = allIds.filter(v => selectedMetrics.includes(v));

    commands(ctx).set('placeMetricsOrder', allIds);
    commands(ctx).set('placeMetrics', placeMetrics);
    commands(ctx).updateSelectedMetrics(selectedIds);
    commands(ctx).set('selectedMetrics', selectedIds);
  },
  updateSelectedMetrics(ids: string[]) {
    let { handlers } = ctx.state;
    handlers.updateSelectedMetrics({ variables: { input: { ids } } });
  },
  updatePlaceMetricsOrder(ids: string[]) {
    let { handlers } = ctx.state;
    handlers.updatePlaceMetricsOrder({ variables: { input: { ids } } });
  },
  toggleSelected(id: $PropertyType<Metric, 'id'>) {
    let { selectedMetrics: ids } = ctx.state;
    if (ids.includes(id)) {
      ids = ids.filter(v => v !== id);
    } else {
      ids = ids.concat(id);
    }
    commands(ctx).updateSelectedMetrics(ids);
    commands(ctx).set('selectedMetrics', ids);
  },
  downloadAreaData(id: string) {
    // Find the metric
    const { placeMetrics, geojson } = ctx.state;
    const metric = placeMetrics.find(v => String(v.id) === String(id));
    if (!metric || !geojson || !metric.valueFormula) {
      throw new Error('No such metric.');
    }

    const { valueFormula, name } = metric;

    let query = I.getIn(metric, ['analysis', 'sql'], '') || '';
    let column = metric.dataview.column || '';
    // Intersect the sql with the polygon
    query = intersectGeoJSON({ query, geojson });
    // Run a SQL query to retrieve all the data for it from the server
    return getCartoSQL(query)
      .map(res => {
        // Get the names of the fields. Prepend them to the CSV rows.
        const fields =
          ['formula'].concat(Object.keys(res.fields)).join(',') + '\n';
        return (
          fields +
          res.rows
            .map(row => {
              const value = row[column];
              // Evaluate the formula of the metric for each record
              // Prepend the formula to the row
              const formulaValue = evalFormula(valueFormula, value);
              return `${formulaValue},` + objectToCSVRow(row);
            })
            .join('')
        );
      })
      .map(csv => {
        // Create a more readable formula
        const formula = valueFormula.replace('value', column);
        const fileName = [name, formula].filter(Boolean).join('-');
        // Download the data as CSV
        downloadCSV({ data: csv, fileName: `${fileName}.csv` });
      });
  },
});

export default commands;
